/**
 * TelemetryProvider — React context that manages client-side telemetry collection.
 *
 * Features:
 *   - On mount / when auth state changes: fetches GET /telemetry/config to check if telemetry is enabled
 *   - Installs global error listeners IMMEDIATELY (before awaiting config) to avoid race conditions
 *   - If enabled after config check, keeps listeners and starts flush timer
 *   - If disabled after config check, removes listeners and stops buffering
 *   - Provides captureEvent() API for custom events (video completed, feature used, etc.)
 *   - Provides sessionId for problem determination (user can copy it for support tickets)
 *   - Buffers events in memory and flushes in batches (time: 15s or size: 20 events)
 *   - Sends batch via POST /telemetry/events
 *   - Flushes buffered events after successful init to avoid losing early events
 *   - Flushes pending events on page visibility change (visibilitychange → 'hidden')
 *   - Flushes pending events on page unload (pagehide) using keepalive fetch
 *   - Registers an axios response interceptor for HTTP error capture
 *
 * Usage:
 *   <TelemetryProvider> wraps the app inside AppLayout
 *   useTelemetry() → { captureEvent, isEnabled, sessionId }
 *
 * @module TelemetryProvider
 */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useCallback,
  useState,
  type ReactNode,
} from 'react';

import { TelemetryEventType, TelemetrySeverity } from '@flashcard/shared';

import { apiClient, tokenStorage } from '@/lib/api/client';
import {
  getTelemetryConfig,
  sendTelemetryBatch,
  type TelemetryEventInput,
} from '@/lib/api/telemetry.api';

import { useAuth } from '@/features/auth/auth.hook';

// ============================================================
// Constants
// ============================================================

const FLUSH_INTERVAL_MS = 15_000; // 15 seconds (reduced from 30s)
const FLUSH_BATCH_SIZE = 20;      // max events before flush
const SESSION_STORAGE_KEY = 'telemetry_session_id';
const SESSION_ID = getOrCreateSessionId();

// ============================================================
// Types
// ============================================================

interface TelemetryContextValue {
  /** Whether telemetry collection is active for this user's org */
  isEnabled: boolean;
  /** Current browser session ID (for problem determination / support tickets) */
  sessionId: string;
  /** Capture a custom application event */
  captureEvent: (name: string, metadata?: Record<string, unknown>) => void;
  /** Capture an error manually */
  captureError: (error: Error | string, metadata?: Record<string, unknown>) => void;
}

const TelemetryContext = createContext<TelemetryContextValue>({
  isEnabled: false,
  sessionId: SESSION_ID,
  captureEvent: () => {},
  captureError: () => {},
});

// ============================================================
// Helpers
// ============================================================

function generateSessionId(): string {
  return 'sess_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
}

/**
 * Get or create a session ID persisted in sessionStorage.
 * Survives page reloads in the same tab, cleared when the tab is closed.
 */
function getOrCreateSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!id) {
      id = generateSessionId();
      sessionStorage.setItem(SESSION_STORAGE_KEY, id);
    }
    return id;
  } catch {
    // sessionStorage might not be available (private browsing, etc.)
    return generateSessionId();
  }
}

// ============================================================
// TelemetryProvider component
// ============================================================

export function TelemetryProvider({ children }: { children: ReactNode }) {
  // ── Auth hook (must be called before any early return) ────
  const { isAuthenticated } = useAuth();

  const [isEnabled, setIsEnabled] = useState(false);
  const bufferRef = useRef<TelemetryEventInput[]>([]);
  const flushTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isFlushingRef = useRef(false);
  const isEnabledRef = useRef(false);

  // Log session ID at mount for developer troubleshooting
  console.info('[Telemetry] Session ID:', SESSION_ID);

  // ── Flush: send buffered events to server ──────────────────

  const flush = useCallback(async () => {
    if (isFlushingRef.current) return;
    if (bufferRef.current.length === 0) return;

    isFlushingRef.current = true;
    const batch = bufferRef.current.splice(0, bufferRef.current.length);

    try {
      await sendTelemetryBatch(batch, SESSION_ID);
    } catch (err) {
      // Silently fail — telemetry must never break the app
      // Re-buffer the events (up to a max to avoid infinite growth)
      if (bufferRef.current.length < 100) {
        bufferRef.current.unshift(...batch.slice(0, 20));
      }
    } finally {
      isFlushingRef.current = false;
    }
  }, []);

  // ── Flush via keepalive fetch (for page unload) ──────────

  const flushBeacon = useCallback(async () => {
    if (bufferRef.current.length === 0) return;

    const batch = bufferRef.current.splice(0, bufferRef.current.length);
    const token = tokenStorage.getAccessToken();
    const baseURL = apiClient.defaults.baseURL ?? '';
    const url = `${baseURL}/telemetry/events`;

    try {
      await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ events: batch, sessionId: SESSION_ID }),
        keepalive: true,
      });
    } catch {
      // Silently fail — telemetry must never break the app
    }
  }, []);

  // ── Buffer: add event to buffer, flush if size threshold ──

  const bufferEvent = useCallback(
    (event: TelemetryEventInput) => {
      bufferRef.current.push(event);
      if (isEnabledRef.current && bufferRef.current.length >= FLUSH_BATCH_SIZE) {
        flush();
      }
    },
    [flush],
  );

  // ── Public API: captureEvent ───────────────────────────────

  const captureEvent = useCallback(
    (name: string, metadata?: Record<string, unknown>) => {
      bufferEvent({
        type: TelemetryEventType.EVENT,
        severity: TelemetrySeverity.INFO,
        message: name,
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        metadata,
        timestamp: new Date().toISOString(),
      });
    },
    [bufferEvent],
  );

  // ── Public API: captureError ────────────────────────────────

  const captureError = useCallback(
    (error: Error | string, metadata?: Record<string, unknown>) => {
      const message = typeof error === 'string' ? error : error.message;
      const stack = typeof error === 'string' ? undefined : error.stack;
      bufferEvent({
        type: TelemetryEventType.UI_ERROR,
        severity: TelemetrySeverity.ERROR,
        message,
        stack,
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        metadata,
        timestamp: new Date().toISOString(),
      });
    },
    [bufferEvent],
  );

  // ── Effect: init telemetry on mount and when auth state changes ─

  useEffect(() => {
    let cancelled = false;
    let listenersCleanup: (() => void) | null = null;

    // Clean up any existing flush timer before creating a new one
    // to avoid multiple concurrent timers.
    if (flushTimerRef.current) {
      clearInterval(flushTimerRef.current);
      flushTimerRef.current = null;
    }

    // ── Install listeners IMMEDIATELY (before any await) ──────
    // This prevents a race condition where errors occurring during
    // the getTelemetryConfig() network request are lost.

    // ── Wrap console.error to capture application errors ──────────
    const originalConsoleError = console.error.bind(console);
    const recentConsoleErrors = new Map<string, number>();
    const CONSOLE_ERROR_DEDUP_MS = 5000; // 5 seconds

    console.error = function (...args: unknown[]) {
      // Call original first so the error still appears in DevTools
      originalConsoleError(...args);

      // Build message from args
      const message = args
        .map((a) => (a instanceof Error ? a.message : typeof a === 'string' ? a : (() => { try { return JSON.stringify(a); } catch { return String(a); } })()))
        .join(' ');

      // Deduplicate: skip if same message seen recently
      const now = Date.now();
      const lastSeen = recentConsoleErrors.get(message);
      if (lastSeen && now - lastSeen < CONSOLE_ERROR_DEDUP_MS) {
        return;
      }
      recentConsoleErrors.set(message, now);

      // Clean old entries periodically
      if (recentConsoleErrors.size > 50) {
        for (const [key, ts] of recentConsoleErrors.entries()) {
          if (now - ts > CONSOLE_ERROR_DEDUP_MS) {
            recentConsoleErrors.delete(key);
          }
        }
      }

      // Extract stack from first Error argument if present
      const firstError = args.find((a): a is Error => a instanceof Error);

      bufferEvent({
        type: TelemetryEventType.CONSOLE_ERROR,
        severity: TelemetrySeverity.ERROR,
        message: message.slice(0, 2000), // truncate very long messages
        stack: firstError?.stack,
        url: window.location.href,
        timestamp: new Date().toISOString(),
      });
    };

    // ── Install global error listeners ─────────────────

    const onErrorHandler = (event: ErrorEvent) => {
      bufferEvent({
        type: TelemetryEventType.UI_ERROR,
        severity: TelemetrySeverity.ERROR,
        message: event.message ?? 'Unknown error',
        stack: event.error?.stack,
        url: event.filename ?? window.location.href,
        metadata: {
          line: event.lineno,
          column: event.colno,
        },
        timestamp: new Date().toISOString(),
      });
    };

    const onResourceErrorHandler = (event: Event) => {
      // In capture phase, event.target is the element that failed to load
      const target = event.target as HTMLElement | null;
      if (!target) return;

      // Only handle element-level errors (not window-level JS errors)
      // Resource errors have event.target as the element, JS errors have event.target as window
      if (target === window || target === document) return;

      const tagName = target.tagName?.toLowerCase() ?? 'unknown';
      // Skip elements inside cross-origin iframes (we can't access them)
      try {
        if (target.ownerDocument !== document) return;
      } catch {
        // Cross-origin iframe access throws SecurityError
        return;
      }

      const resourceUrl =
        (target as HTMLScriptElement).src ||
        (target as HTMLLinkElement).href ||
        (target as HTMLImageElement).src ||
        '';

      bufferEvent({
        type: TelemetryEventType.RESOURCE_ERROR,
        severity: TelemetrySeverity.WARNING,
        message: `Resource load failed: <${tagName}> ${resourceUrl || '(unknown URL)'}`,
        url: window.location.href,
        metadata: {
          tagName,
          resourceUrl: resourceUrl || undefined,
        },
        timestamp: new Date().toISOString(),
      });
    };

    const onRejectionHandler = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const message =
        reason instanceof Error
          ? reason.message
          : typeof reason === 'string'
            ? reason
            : JSON.stringify(reason);
      bufferEvent({
        type: TelemetryEventType.UNHANDLED_REJECTION,
        severity: TelemetrySeverity.ERROR,
        message,
        stack: reason instanceof Error ? reason.stack : undefined,
        url: window.location.href,
        timestamp: new Date().toISOString(),
      });
    };

    window.addEventListener('error', onErrorHandler);
    window.addEventListener('error', onResourceErrorHandler, true); // capture phase
    window.addEventListener('unhandledrejection', onRejectionHandler);

    // ── Install axios interceptor for HTTP errors ──────

    const interceptorId = apiClient.interceptors.response.use(
      undefined,
      (error: any) => {
        // Capture HTTP errors (non-2xx responses)
        if (error?.response) {
          const status = error.response.status;
          // Only capture 4xx and 5xx (not 3xx redirects)
          if (status >= 400) {
            bufferEvent({
              type: TelemetryEventType.HTTP_ERROR,
              severity: status >= 500 ? TelemetrySeverity.ERROR : TelemetrySeverity.WARNING,
              message: `HTTP ${status} on ${error.config?.method?.toUpperCase() ?? 'GET'} ${error.config?.url ?? '(unknown)'}`,
              method: error.config?.method?.toUpperCase(),
              statusCode: status,
              url: error.config?.url,
              metadata: {
                responseData: typeof error.response.data === 'object'
                  ? JSON.stringify(error.response.data).slice(0, 1000)
                  : String(error.response.data).slice(0, 1000),
              },
              timestamp: new Date().toISOString(),
            });
          }
        } else if (error?.code === 'ERR_NETWORK' || error?.code === 'ECONNABORTED') {
          // Network error
          bufferEvent({
            type: TelemetryEventType.NETWORK_ERROR,
            severity: TelemetrySeverity.ERROR,
            message: `Network error on ${error.config?.method?.toUpperCase() ?? 'GET'} ${error.config?.url ?? '(unknown)'}`,
            method: error.config?.method?.toUpperCase(),
            url: error.config?.url,
            metadata: { code: error.code },
            timestamp: new Date().toISOString(),
          });
        }
        // Re-throw so existing error handling continues
        return Promise.reject(error);
      },
    );

    // Store cleanup for listeners (will be called in the effect cleanup)
    listenersCleanup = () => {
      window.removeEventListener('error', onErrorHandler);
      window.removeEventListener('error', onResourceErrorHandler, true);
      window.removeEventListener('unhandledrejection', onRejectionHandler);
      apiClient.interceptors.response.eject(interceptorId);
      console.error = originalConsoleError;
    };

    // ── Now fetch config (async) ─────────────────────────────

    async function init() {
      try {
        const config = await getTelemetryConfig();
        if (cancelled) return;
        setIsEnabled(config.enabled);
        isEnabledRef.current = config.enabled;

        if (!config.enabled) {
          // Telemetry is disabled — remove listeners and stop collecting
          if (listenersCleanup) {
            listenersCleanup();
            listenersCleanup = null;
          }
          return;
        }

        // Flush any events that were buffered during init
        flush();

        // Start flush timer (safe: we cleared any existing one above)
        flushTimerRef.current = setInterval(() => {
          flush();
        }, FLUSH_INTERVAL_MS);
      } catch {
        // Silently fail — telemetry must never break the app
        // Remove listeners on error too
        if (listenersCleanup) {
          listenersCleanup();
          listenersCleanup = null;
        }
      }
    }

    init();

    return () => {
      cancelled = true;
      if (flushTimerRef.current) {
        clearInterval(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      // Flush any remaining events on unmount / before re-running
      flush();
      // Clean up interceptors/listeners
      if (listenersCleanup) {
        listenersCleanup();
        listenersCleanup = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // ── Effect: flush on page visibility change / unload ───────

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Page is being hidden — flush any pending events
        flush();
      }
    };

    const handlePageHide = () => {
      // Page is being unloaded — use keepalive fetch to guarantee delivery
      flushBeacon();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [flush, flushBeacon]);

  const value: TelemetryContextValue = {
    isEnabled,
    sessionId: SESSION_ID,
    captureEvent,
    captureError,
  };

  return <TelemetryContext.Provider value={value}>{children}</TelemetryContext.Provider>;
}

// ============================================================
// useTelemetry hook
// ============================================================

export function useTelemetry(): TelemetryContextValue {
  return useContext(TelemetryContext);
}